'use client';

import { Button } from '@/components/ui/button';
import {
  useGetListPromotions,
  useMutateApplyPromotion,
  useMutateValidateVoucher,
} from '@/hooks/api/storefront/promotion';
import { ChevronLeft } from 'lucide-react';
import { Dispatch, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { StoreCartDetail } from '@/types/http';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  cn,
  generatePromotionsPayload,
  isEmpty,
  mergePromotions,
  parseErrorMessage,
} from '@/lib/utils';
import { AxiosError } from 'axios';
import { BasePromotion } from '@/types/http/promotion/common';
import ModalAlert from '@/components/sections/modal-alert';
import useModal from '@/hooks/useModal';
import { useParams } from 'next/navigation';
import { toast } from '@/components/ui/use-toast';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { queryClient } from '@/lib/query-client';
import { BaseFreeGift, usePromotionStore } from '@/stores/promotion';
import ModalGiftReward from '@/components/sections/builder/elements/body/cart-total/component/modal-gift-reward';
import { shallow } from 'zustand/shallow';
import { ThemeColorSchemeType, ThemePromotionType } from '@/stores/builder';
import CardPromotion from '@/components/sections/builder/elements/body/checkout-form/component/card-promotion';

type VoucherListProps = {
  carts: StoreCartDetail;
  sectionView: 'detail' | 'voucher' | 'active_offers';
  setSectionView: Dispatch<React.SetStateAction<'detail' | 'voucher' | 'active_offers'>>;
  colorScheme: ThemeColorSchemeType;
  promotionTheme?: ThemePromotionType;
};

export default function VoucherList({
  carts,
  sectionView,
  setSectionView,
  colorScheme,
  promotionTheme,
}: VoucherListProps) {
  const token = Cookies.get('accessToken') as string;
  const params = useParams();
  const checkoutId = params.checkoutId as string;
  const modalUseVoucher = useModal();
  const modalGift = useModal();

  const [voucherCode, setVoucherCode] = useState<string>('');
  const [errorVoucher, setErrorVoucher] = useState<string | null>(null);
  const [redeemedVouchers, setRedeemedVouchers] = useState<BasePromotion[]>([]);
  const [pendingReplacePromos, setPendingReplacePromos] = useState<BasePromotion[] | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<BasePromotion | null>(null);
  const [giftPromotion, setGiftPromotion] = useState<BasePromotion | null>(null);
  const [maxQtyReward, setMaxQtyReward] = useState<number>(0);
  const [selectedTab, setSelectedTab] = useState<'product_group' | 'product_variant'>(
    'product_group'
  );

  const {
    selectedPromotion,
    selectedFreeGift,
    applyVoucherRewards,
    setSelectedPromotion,
    replaceFreeGift,
  } = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
      selectedFreeGift: state.selectedFreeGift,
      applyVoucherRewards: state.applyVoucherRewards,
      setSelectedPromotion: state.setSelectedPromotion,
      replaceFreeGift: state.replaceFreeGift,
    }),
    shallow
  );

  /**
   * Payload for list promotions
   * @param items - Array of cart items
   * @returns - Object containing items and grand total
   */
  const payloadListPromotion = useMemo(() => {
    const items = (carts?.items || []).filter((item) => !item.promotion_id);

    const { mappedItems, grandTotal } = items.reduce(
      (acc, item) => {
        const discountAmount = item.discount_amount ?? 0;
        const sellPrice = item.sell_price;

        const pricePerItem = sellPrice;
        const itemTotal = item.quantity * pricePerItem;

        acc.mappedItems.push({
          item_id: item.id,
          qty: item.quantity,
          amount: itemTotal,
        });

        acc.grandTotal += itemTotal - discountAmount;

        return acc;
      },
      {
        mappedItems: [] as {
          item_id: string;
          qty: number;
          amount: number;
        }[],
        grandTotal: 0,
      }
    );

    return {
      items: mappedItems,
      grand_total: grandTotal,
      current_date: new Date().toISOString(),
    };
  }, [carts?.items]);

  /**
   * Fetch list of promotions
   * @param payloadListPromotion - Payload for list promotions
   * @param token - Access token
   * @returns - List of promotions
   */
  const { data: listPromotions, isLoading: isLoadingPromotions } = useGetListPromotions(
    payloadListPromotion,
    false,
    token as string,
    {
      enabled: !!payloadListPromotion.items.length && sectionView === 'voucher',
    }
  );

  const allPromotions = useMemo(() => {
    const merged: BasePromotion[] = [];
    listPromotions?.forEach((promo) => merged.push(promo));
    selectedPromotion?.forEach((promo) => {
      if (!merged.some((p) => p.promotion_id === promo.promotion_id)) merged.push(promo);
    });
    redeemedVouchers.forEach((promo) => {
      if (!merged.some((p) => p.promotion_id === promo.promotion_id)) merged.push(promo);
    });

    // Sort: use_voucher: true di atas
    merged.sort((a, b) => {
      const aVoucher = !!a.use_voucher;
      const bVoucher = !!b.use_voucher;
      if (aVoucher === bVoucher) return 0;
      return aVoucher ? -1 : 1; // use_voucher: true duluan
    });

    return merged;
  }, [listPromotions, selectedPromotion, redeemedVouchers]);

  /**
   * Check if promotion is disabled
   * @param promo - Promotion
   * @returns - Boolean
   */
  const isPromoDisabled = (promo: BasePromotion): boolean => {
    const isAlreadySelected = selectedPromotion.some((p) => p.promotion_id === promo.promotion_id);
    if (isAlreadySelected) return false;

    const hasNonStackableSelected = selectedPromotion.some((p) => p.is_overlap === false);
    const hasStackableSelected = selectedPromotion.some((p) => p.is_overlap === true);
    const isEligible = (payloadListPromotion.grand_total || 0) >= promo.minimum_spending;

    if (!isEligible) return true;
    if (hasNonStackableSelected) return true;
    if (hasStackableSelected && promo.is_overlap === false) return true;

    return false;
  };

  /**
   * Validate voucher
   * @param payload - Payload for validating voucher
   * @param token - Access token
   * @returns - Void
   */
  const validateVoucher = useMutateValidateVoucher({
    onSuccess: (data) => {
      if (data.data.is_valid) {
        toast({
          variant: 'success',
          title: 'Success',
          description: 'Voucher redeemed successfully',
        });

        const promoRewards = data.data.promotion_rewards;
        const voucherCode = data.data.voucher_code;

        promoRewards.forEach((promo) => {
          const promoWithCode = { ...promo, voucher_code: voucherCode };

          setRedeemedVouchers((prev) => {
            if (!prev.some((p) => p.promotion_id === promo.promotion_id)) {
              return [...prev, promoWithCode];
            }
            return prev;
          });
        });

        const isNewOverlapFalse = promoRewards.some((promo) => promo.is_overlap === false);
        const hasExistingOverlapFalse = selectedPromotion?.some(
          (promo) => promo.is_overlap === false
        );
        const hasExistingOverlapTrue = selectedPromotion?.every(
          (promo) => promo.is_overlap === true
        );

        const hasConflict =
          (hasExistingOverlapTrue && isNewOverlapFalse) ||
          (hasExistingOverlapFalse && !isNewOverlapFalse) ||
          (hasExistingOverlapFalse && isNewOverlapFalse);

        if (hasConflict) {
          setPendingReplacePromos(promoRewards);
          modalUseVoucher.open();
          return;
        }

        if (promoRewards[0]?.product_reward && promoRewards[0].product_reward.length > 0) {
          setPendingPromotion(promoRewards[0]);
          setGiftPromotion(promoRewards[0]);
          setMaxQtyReward(
            promoRewards[0].counted_multiply
              ? promoRewards[0].product_reward[0].qty * promoRewards[0].counted_multiply
              : promoRewards[0].product_reward[0].qty
          );
          modalGift.setIsShow(true);
          return;
        }

        applyVoucherRewards(
          promoRewards.map((p) => ({ ...p, voucher_code: voucherCode })),
          voucherCode,
          isNewOverlapFalse
        );
        setVoucherCode('');
      }
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        setErrorVoucher(error.response?.data?.message);
      }
    },
  });

  /**
   * Handle redeem voucher
   * @param voucherCode - Voucher code to redeem
   * @returns - Void
   */
  const handleRedeemVoucher = async () => {
    if (!voucherCode.trim()) {
      setErrorVoucher('Please enter a voucher code');
      return;
    }

    if (selectedPromotion.some((promo) => promo.voucher_code === voucherCode)) {
      setErrorVoucher('Voucher code already used');
      return;
    }

    const payload = {
      voucher_code: voucherCode,
      order: {
        items: payloadListPromotion?.items?.map((item) => ({
          item_id: item.item_id,
          qty: item.qty,
        })),
        grand_total: payloadListPromotion.grand_total || 0,
      },
    };

    try {
      await validateVoucher.mutateAsync({
        body: payload,
        token: token as string,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        const rawMessage = error.response?.data?.data || error.response?.data?.message;
        setErrorVoucher(parseErrorMessage(rawMessage));
      }
    }
  };

  /**
   * Handles toggling promotion
   * @param {BasePromotion} promotion - Promotion object
   * @returns {void}
   */
  const handleTogglePromotion = (promotion: BasePromotion): void => {
    const isSelected = selectedPromotion?.some((p) => p.promotion_id === promotion?.promotion_id);

    if (isSelected) {
      setSelectedPromotion(
        selectedPromotion?.filter((p) => p.promotion_id !== promotion.promotion_id)
      );
    } else {
      if ((promotion.product_reward?.length ?? 0) > 0) {
        setPendingPromotion(promotion);
        setGiftPromotion(promotion);
        setMaxQtyReward(
          promotion.product_reward && promotion.product_reward[0]
            ? promotion.counted_multiply
              ? promotion.product_reward[0].qty * promotion.counted_multiply
              : promotion.product_reward[0].qty
            : 0
        );
        modalGift.setIsShow(true);
      } else {
        setSelectedPromotion([...(selectedPromotion ?? []), promotion]);
      }
    }
  };

  const { mutateAsync: mutateApplyPromotion, isPending: isLoadingApplyPromotion } =
    useMutateApplyPromotion({
      onSuccess: async (data) => {
        if (data.statusCode === 200) {
          await queryClient.invalidateQueries({ queryKey: ['checkoutDetail', checkoutId] });
          setSectionView('detail');
        }
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data.data || error.response?.data?.message;
          toast({
            title: 'Error',
            description: parseErrorMessage(errorMessage) || 'Something went wrong',
            variant: 'destructive',
          });
        }
      },
    });

  const promoVoucherCodeMap = (carts.promotions ?? []).reduce(
    (acc: Record<string, string>, promo) => {
      if (promo.promotion_id && promo.voucher_code) {
        acc[promo.promotion_id] = promo.voucher_code;
      }
      return acc;
    },
    {}
  );

  const selectedPromotionIds = selectedPromotion?.map((promo) => promo.promotion_id) ?? [];
  const selectedFreeGiftIds = selectedFreeGift?.map((gift) => gift.promotion_id) || [];

  const promotionsFromCartItems = (carts.items ?? [])
    .filter(
      (item) =>
        item.promotion_id &&
        selectedPromotionIds.includes(item.promotion_id) &&
        !selectedFreeGiftIds.includes(item.promotion_id)
    )
    .map((item) => {
      const base = {
        promotion_id: item.promotion_id as string,
        items: [
          {
            item_id: item.id,
            qty: item.quantity,
          },
        ],
      };
      // if the item has a voucher code, add it to the promotion payload
      const voucher_code = promoVoucherCodeMap[item.promotion_id as string];
      return voucher_code ? { ...base, voucher_code } : base;
    });

  const allPromotionsMap = useMemo(() => {
    const map = new Map<string, BasePromotion>();
    allPromotions?.forEach((promo) => map.set(promo.promotion_id, promo));
    return map;
  }, [allPromotions]);

  const normalizedSelectedPromotion = useMemo(() => {
    return (selectedPromotion ?? []).map((promo) => {
      const fullPromo = allPromotionsMap.get(promo.promotion_id);
      if (fullPromo) {
        return {
          ...fullPromo,
          voucher_code: promo.voucher_code || fullPromo.voucher_code,
        };
      }
      return promo;
    });
  }, [selectedPromotion, allPromotionsMap]);

  const generatedPromotions = generatePromotionsPayload(
    normalizedSelectedPromotion,
    selectedFreeGift
  );
  const finalPromotionsPayload = mergePromotions([
    ...generatedPromotions,
    ...promotionsFromCartItems,
  ]);

  /**
   * Handle use voucher
   * This function will send a request to the server to apply the promotions
   * and update the state of selectedPromotion and selectedFreeGift.
   * @returns - Void
   */
  const handleUseVoucher = async () => {
    await mutateApplyPromotion({
      body: {
        cart_id: checkoutId as string,
        promotions: finalPromotionsPayload,
      },
      token: token as string,
    });
  };

  /**
   * Handle confirm replace voucher
   * When user confirm to replace current voucher with the new one,
   * this function will update the state of selectedPromotion and
   * pendingReplacePromos, and close the modal use voucher. After that,
   * it will redirect to detail section.
   * @returns - Void
   */
  const handleConfirmReplace = () => {
    const promoWithProductReward = pendingReplacePromos?.find(
      (promo) => (promo.product_reward?.length ?? 0) > 0
    );

    const promoWithVoucherCode = pendingReplacePromos?.map((promo) => ({
      ...promo,
      voucher_code: voucherCode,
    }));

    if (promoWithProductReward) {
      setPendingPromotion(promoWithProductReward);
      setGiftPromotion(promoWithProductReward);
      setMaxQtyReward(promoWithProductReward.product_reward?.[0]?.qty ?? 0);
      modalUseVoucher.setIsShow(false);
      modalGift.setIsShow(true);
    } else {
      setSelectedPromotion(promoWithVoucherCode as BasePromotion[]);
      setPendingReplacePromos(null);
      modalUseVoucher.setIsShow(false);
    }
  };

  const handleSaveFreeGift = (data: BaseFreeGift[]) => {
    if (!pendingPromotion) return;

    const isDuplicate = selectedPromotion?.some(
      (promo) => promo.promotion_id === pendingPromotion.promotion_id
    );
    const hasOverlapPromo = selectedPromotion?.some((p) => p.is_overlap === true);

    if (isDuplicate) {
      toast({
        variant: 'destructive',
        title: 'Error!',
        description: 'This promotion is already selected',
      });
    }

    if (!pendingPromotion.is_overlap) {
      setSelectedPromotion([pendingPromotion]);
      replaceFreeGift(String(giftPromotion?.promotion_id), data);
      return;
    }

    if (hasOverlapPromo) {
      setSelectedPromotion([...(selectedPromotion ?? []), pendingPromotion]);
    } else {
      setSelectedPromotion([pendingPromotion]);
    }

    replaceFreeGift(String(giftPromotion?.promotion_id), data);
  };

  useEffect(() => {
    const listPromotions = carts?.promotions || [];
    const redeemedVouchers = listPromotions.filter((promo) => promo.use_voucher);

    if (sectionView === 'voucher' && listPromotions.length > 0) {
      setSelectedPromotion(listPromotions);
      setRedeemedVouchers(redeemedVouchers);
      setPendingReplacePromos(null);
      setPendingPromotion(null);
      setGiftPromotion(null);
      setMaxQtyReward(0);
    }
  }, [sectionView, carts?.promotions?.length]);

  useEffect(() => {
    if (!modalUseVoucher.isShow) {
      setPendingReplacePromos(null);
    }
  }, [modalUseVoucher.isShow]);

  useEffect(() => {
    if (errorVoucher) {
      const timeout = setTimeout(() => {
        setErrorVoucher(null);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [errorVoucher]);

  return (
    <>
      <div className='flex items-center pb-6 gap-x-2'>
        <ChevronLeft className='cursor-pointer' onClick={() => setSectionView('detail')} />
        <h2 className='text-xl'>
          Select voucher{' '}
          {(selectedPromotion?.length ?? 0) > 0 && `(${selectedPromotion?.length} selected)`}
        </h2>
      </div>

      <div
        className='py-2 lg:px-4'
        style={
          {
            '--text-primary': promotionTheme?.colorScheme?.text || '#000000',
            '--text-secondary': promotionTheme?.colorScheme?.textSecondary || '#000000',
            '--background': promotionTheme?.colorScheme?.background || '#FFFFFF',
            '--btn-label': promotionTheme?.colorScheme?.btnSolidlabel || '#FFFFFF',
            '--btn-background': promotionTheme?.colorScheme?.btnSolidBackgroud || '#000000',
          } as React.CSSProperties
        }
      >
        <div className='flex items-center gap-2'>
          <input
            type='text'
            placeholder='Enter voucher code'
            className={cn(
              'w-full flex-1 rounded px-3 py-2.5 text-xs outline-none ring-1 focus:ring-1',
              errorVoucher ? 'ring-red-500' : 'ring-[var(--background)]'
            )}
            value={voucherCode}
            onChange={(e) => setVoucherCode(e.target.value)}
          />

          <Button
            type='button'
            size='sm'
            variant={isEmpty(colorScheme) ? 'secondary' : null}
            className={cn(
              'items-center rounded-sm py-5 text-xs font-normal transition-all duration-300 hover:opacity-80',
              !isEmpty(colorScheme) && 'bg-[var(--btn-background)] text-[var(--btn-label)]'
            )}
            onClick={handleRedeemVoucher}
            disabled={validateVoucher.isPending}
          >
            {validateVoucher.isPending && <LoadingAnimationIcon size={4} />} Redeem
          </Button>
        </div>
        {errorVoucher && <p className='mt-1 text-xs text-red-500'>{errorVoucher}</p>}
      </div>

      <ScrollArea className='max-h-[600px] overflow-y-auto pt-4 lg:px-4' type='scroll'>
        {isLoadingPromotions ? (
          <div className='flex flex-col gap-1.5'>
            {Array.from({ length: 10 }, (_, index) => (
              <Skeleton key={index} className='w-full h-20' />
            ))}
          </div>
        ) : (
          <div className='flex flex-col gap-1.5'>
            {allPromotions && allPromotions?.length > 0 ? (
              allPromotions?.map((promotion) => {
                const isSelected = selectedPromotion.some(
                  (p) => p.promotion_id === promotion.promotion_id
                );
                const percentage = Math.min(
                  (payloadListPromotion.grand_total / promotion.minimum_spending) * 100,
                  100
                );
                const isEligiblePromo =
                  (payloadListPromotion.grand_total || 0) >= promotion.minimum_spending;
                const isDisabled = isPromoDisabled(promotion);

                return (
                  <CardPromotion
                    key={promotion.promotion_id}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    percentage={percentage}
                    promotion={promotion}
                    isEligiblePromo={isEligiblePromo}
                    grandTotal={payloadListPromotion.grand_total || 0}
                    handleSelectPromo={() => handleTogglePromotion(promotion)}
                    colorScheme={colorScheme}
                    theme={promotionTheme}
                  />
                );
              })
            ) : (
              <p className='text-sm text-center text-gray-500'>No promotions available</p>
            )}
          </div>
        )}
      </ScrollArea>

      <div
        className='mt-4 lg:px-4'
        style={
          {
            '--text-primary': promotionTheme?.colorScheme?.text || '#000000',
            '--text-secondary': promotionTheme?.colorScheme?.textSecondary || '#000000',
            '--background': promotionTheme?.colorScheme?.background || '#FFFFFF',
            '--btn-label': promotionTheme?.colorScheme?.btnSolidlabel || '#FFFFFF',
            '--btn-background': promotionTheme?.colorScheme?.btnSolidBackgroud || '#000000',
          } as React.CSSProperties
        }
      >
        <Button
          type='button'
          variant={isEmpty(colorScheme) ? 'default' : null}
          onClick={() => modalUseVoucher.open()}
          disabled={isLoadingPromotions || isLoadingApplyPromotion}
          className={cn(
            'flex w-full items-center gap-2 rounded-sm py-5 transition-all duration-300 hover:opacity-80',
            !isEmpty(colorScheme) && 'bg-[var(--btn-background)] text-[var(--btn-label)]'
          )}
        >
          {(isLoadingPromotions || isLoadingApplyPromotion) && (
            <LoadingAnimationIcon size={4} color='white' />
          )}
          Use voucher
        </Button>
      </div>

      <ModalGiftReward
        openModal={modalGift.isShow}
        setOpenModal={modalGift.setIsShow}
        promotion={giftPromotion as BasePromotion}
        pendingPromotion={pendingPromotion as BasePromotion}
        setPromotion={setGiftPromotion}
        setSelectedPromotion={setSelectedPromotion}
        token={token}
        onSaveGift={handleSaveFreeGift}
        maxQty={maxQtyReward}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        colorScheme={promotionTheme?.colorScheme}
      />

      <ModalAlert
        openModal={modalUseVoucher.isShow}
        setOpenModal={modalUseVoucher.setIsShow}
        title='Are you sure'
        description={
          pendingReplacePromos
            ? 'This will replace your currently selected promotions. Continue?'
            : 'This action will overwrite your previously active promotions'
        }
        onConfirm={pendingReplacePromos ? handleConfirmReplace : handleUseVoucher}
      />
    </>
  );
}
